import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  HeaderGroup,
} from '@tanstack/react-table';
import { PivotResult, PivotTreeNode } from './graphql';
import { NULL_LABEL } from './constants';
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client';
import { handleExport } from './export-columns';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';

export interface PivotGridProps {
  data?: PivotResult;
  loading?: boolean;
  error?: Error | null;
  className?: string;
  heatmapEnabled?: boolean;
  appliedBadges?: Array<{
    dimId: string;
    operator: string;
    values?: string[];
    label: string;
    text: string;
  }>;

  // NEW: Pass down query state from parent
  tableId: string;
  rowDims: string[];
  colDims: string[];
  measureId: string;
  filters: any[];
  filterValues: any;
}

interface PivotRowData {
  __rowKey: string;
  __depth: number;
  __label: string;
  __sum?: number;
  cells: Record<string, number>;
}

function formatNumber(value: number): string {
  if (Math.abs(value) >= 10000) {
    if (Math.abs(value) >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    }
    return (value / 1000).toFixed(1) + 'k';
  }
  return value.toLocaleString();
}

function buildNestedColumns(
  colTree: PivotTreeNode[] | undefined,
  heatmapEnabled = false,
  heatmapRange: [number, number] = [0, 0],
): { columns: ColumnDef<PivotRowData>[]; leafKeys: string[] } {
  const leafKeys: string[] = [];

  const collectLeafKeys = (nodes?: PivotTreeNode[]) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return;
    nodes.forEach((node) => {
      if (!node.children || node.children.length === 0) {
        leafKeys.push(node.key);
      } else {
        collectLeafKeys(node.children);
      }
    });
  };

  const buildColumnFromNode = (node: PivotTreeNode): ColumnDef<PivotRowData> => {
    if (!node.children || node.children.length === 0) {
      const label = node.value == null ? NULL_LABEL : String(node.value);
      return {
        id: node.key,
        header: label,
        accessorFn: (row) => row.cells[node.key] || 0,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          const formattedValue = typeof value === 'number' ? formatNumber(value) : value;
          return (
            <div className="pivot-cell-number">
              {value == null ? <span className="pivot-cell-null"></span> : formattedValue}
            </div>
          );
        },
        size: 100,
      };
    }
    const label = node.value == null ? NULL_LABEL : String(node.value);
    return {
      id: node.key,
      header: label,
      columns: node.children.map(buildColumnFromNode),
    };
  };

  collectLeafKeys(colTree);

  const columns = Array.isArray(colTree) && colTree.length ? colTree.map(buildColumnFromNode) : [];

  return { columns, leafKeys };
}

function buildRowData(data: PivotResult, leafKeys: string[]): PivotRowData[] {
  const rowLeaves = Array.isArray(data?.rowLeaves) ? data.rowLeaves : [];
  const cells = Array.isArray(data?.cells) ? data.cells : [];

  const cellsLookup = new Map<string, Record<string, number>>();
  cells.forEach((cell) => {
    if (!cellsLookup.has(cell.rowKey)) cellsLookup.set(cell.rowKey, {});
    cellsLookup.get(cell.rowKey)![cell.colKey] = cell.value;
  });

  return rowLeaves.map((rowLeaf) => {
    const rowCells = cellsLookup.get(rowLeaf.key) || {};
    const rowSum = leafKeys.reduce((acc, k) => acc + (rowCells[k] ?? 0), 0);
    const label =
      rowLeaf.path && rowLeaf.path.length > 0
        ? rowLeaf.path
            .map((seg) => (seg.value == null ? NULL_LABEL : String(seg.value)))
            .join(' ▸ ')
        : 'Σύνολο';

    return {
      __rowKey: rowLeaf.key,
      __depth: rowLeaf.depth,
      __label: label,
      __sum: rowSum,
      cells: rowCells,
    };
  });
}

export function PivotGrid({
  data,
  loading,
  error,
  className = '',
  heatmapEnabled = false,
  appliedBadges = [],
  tableId,
  rowDims,
  colDims,
  measureId,
  filters,
  filterValues,
}: PivotGridProps) {
  const { t } = useTranslation();
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const grandCountFromCells =
    measureId === 'count'
      ? (Array.isArray(data?.cells) ? data!.cells : []).reduce((acc, c) => acc + (c.value ?? 0), 0)
      : undefined;

  const safeTotal =
    typeof data?.totalCount === 'number' && data.totalCount > 0
      ? data.totalCount
      : grandCountFromCells ?? (Array.isArray(data?.rowLeaves) ? data.rowLeaves.length : 0);

  const heatmapRange = useMemo<[number, number]>(() => {
    if (!heatmapEnabled || !data) return [0, 0];
    const values = data.cells.map((c) => c.value).filter((v) => typeof v === 'number');
    if (!values.length) return [0, 0];
    return [Math.min(...values), Math.max(...values)];
  }, [data, heatmapEnabled]);

  const { columns: nestedColumns, leafKeys } = useMemo(() => {
    const safeColTree = Array.isArray(data?.colTree) ? data!.colTree : [];
    return buildNestedColumns(safeColTree, heatmapEnabled, heatmapRange);
  }, [data, heatmapEnabled, heatmapRange]);

  const columns = useMemo<ColumnDef<PivotRowData>[]>(() => {
    if (!data) return [];
    const rowColumn: ColumnDef<PivotRowData> = {
      id: '__row',
      header: 'Γραμμή',
      accessorKey: '__label',
      cell: ({ row }) => {
        const { __depth, __label } = row.original;
        return (
          <div className="pivot-row-label" style={{ paddingLeft: `${__depth * 16}px` }}>
            {__label === 'Σύνολο' ? <strong>{__label}</strong> : __label}
          </div>
        );
      },
      size: 200,
    };
    const rowTotalCol: ColumnDef<PivotRowData> = {
      id: '__row_total',
      header: 'Σύνολο Γραμμής',
      accessorFn: (row) => row.__sum ?? 0,
      cell: ({ getValue }) => (
        <div className="pivot-cell-number">
          <strong>{formatNumber(getValue() as number)}</strong>
        </div>
      ),
      size: 120,
    };
    return [rowColumn, ...nestedColumns, rowTotalCol];
  }, [data, nestedColumns]);

  const rowData = useMemo(() => (data ? buildRowData(data, leafKeys) : []), [data, leafKeys]);

  const table = useReactTable({
    data: rowData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualFiltering: true,
  });

  const { colTotals, grandTotal } = useMemo(() => {
    if (!data || !leafKeys.length) return { colTotals: {}, grandTotal: 0 };
    const colTotals: Record<string, number> = {};
    leafKeys.forEach((k) => {
      colTotals[k] = rowData.reduce((acc, r) => acc + (r.cells[k] ?? 0), 0);
    });
    const grandTotal = rowData.reduce((acc, r) => acc + (r.__sum ?? 0), 0);
    return { colTotals, grandTotal };
  }, [data, leafKeys, rowData]);

  if (loading)
    return (
      <div className={`pivot-grid ${className}`}>
        <div className="pivot-loading">Loading...</div>
      </div>
    );
  if (error)
    return (
      <div className={`pivot-grid ${className}`}>
        <div className="pivot-error">Error: {error.message}</div>
      </div>
    );
  if (!data)
    return (
      <div className={`pivot-grid ${className}`}>
        <div className="pivot-empty">{t('Πατήστε Αναζήτηση για να δείτε τα αποτελέσματα')}</div>
      </div>
    );

  return (
    <div className={`pivot-grid ${className} ${heatmapEnabled ? 'pivot-heatmap' : ''}`}>
      <div className="pivot-table-wrapper">
        <table className="pivot-table">
          <thead>
            {table.getHeaderGroups().map((hg: HeaderGroup<PivotRowData>) => (
              <tr key={hg.id}>
                {hg.headers.map((header, i) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`pivot-header ${i === 0 ? 'pivot-col-sticky' : ''}`}
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="pivot-header-content">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className={row.original.__label === 'Σύνολο' ? 'pivot-total' : ''}>
                {row.getVisibleCells().map((cell, i) => (
                  <td key={cell.id} className={`pivot-cell ${i === 0 ? 'pivot-col-sticky' : ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="pivot-total">
              <td className="pivot-col-sticky">
                <strong>Σύνολο Στήλης</strong>
              </td>
              {leafKeys.map((k) => (
                <td key={`tot-${k}`} className="pivot-cell">
                  <div className="pivot-cell-number">
                    <strong>{formatNumber(colTotals[k] ?? 0)}</strong>
                  </div>
                </td>
              ))}
              <td className="pivot-cell">
                <div className="pivot-cell-number">
                  <strong>{formatNumber(grandTotal)}</strong>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div
        className="pivot-footer"
        style={{
          marginTop: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="warning"
          onClick={() =>
            handleExport(client, { tableId, rowDims, colDims, measureId, filters, filterValues }, t)
          }
        >
          <FaFileExcel className="me-2" aria-hidden />
          {t('Εξαγωγή σε Excel')}
        </Button>
        <div className="pivot-total">
          <strong>Σύνολο Εγγραφών:</strong> {Number(safeTotal).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
